import { gql } from "@apollo/client";

export const ENTITIES_DROPDOWN_NAMES = gql`
  query FeatureDropDownInstances(
    $feature_type_id: Int!
    $excluded_instance_id: [uuid!]
  ) {
    feature_instance(
      where: {
        feature_type_id: { _eq: $feature_type_id }
        implicit: { _eq: false }
        deleted_from_source: { _eq: false }
        deleted: { _eq: false }
        id: { _nin: $excluded_instance_id }
        feature_workflow_instance: { workflow_status: { _neq: "8" } }
      }
      order_by: { auto_inc_value: asc }
    ) {
      id
      title
      auto_inc_value
      feature_instance_spec_sections {
        spec_section {
          title
          section_number
        }
      }
      feature_submittals {
        material_tracking
      }
      links_as_source_aggregate(
        where: {
          target_feature_instance: {
            deleted: { _eq: false }
            implicit: { _eq: false }
          }
        }
      ) {
        aggregate {
          count
        }
      }
      links_as_target_aggregate(
        where: {
          source_feature_instance: {
            deleted: { _eq: false }
            implicit: { _eq: false }
          }
        }
      ) {
        aggregate {
          count
        }
      }

      links_as_source(
        where: {
          target_feature_instance: {
            feature_type_id: { _eq: 9 }
            deleted: { _eq: false }
          }
        }
        order_by: { target_feature_instance: { number: asc } }
      ) {
        id
        link_type
        offset_duration
        linked_offset_date
        reverse_dependency
        target_feature_instance {
          ...feature_instanceFragment
        }
      }
      successor_material_links: links_as_source(
        where: {
          target_feature_instance: {
            feature_type_id: { _eq: 2 }
            implicit: { _eq: false }
            deleted: { _eq: false }
          }
        }
      ) {
        id
      }
      predecessor_material_links: links_as_target(
        where: {
          source_feature_instance: {
            feature_type_id: { _eq: 2 }
            implicit: { _eq: false }
            deleted: { _eq: false }
          }
        }
      ) {
        id
      }
      links_as_target(
        where: {
          source_feature_instance: {
            feature_type_id: { _eq: 9 }
            deleted: { _eq: false }
          }
        }
        order_by: { source_feature_instance: { number: asc } }
      ) {
        id
        link_type
        offset_duration
        linked_offset_date
        reverse_dependency
        source_feature_instance {
          ...feature_instanceFragment
        }
      }
    }
  }

  fragment feature_instanceFragment on feature_instance {
    id
    auto_inc_value
    number
    title
    deleted_from_source
    implicit
    feature {
      ...featuresFragment
    }
    feature_workflow_instance {
      workflow_status
    }
    feature_instance_spec_sections {
      ...feature_instance_spec_sectionFragment
    }
    feature_activities {
      ...feature_activitiesFragment
    }
    feature_submittals {
      material_tracking
    }
  }

  fragment feature_activitiesFragment on feature_activities {
    type
    duration
    end_date
    start_date
    import_log_id
    parent
    critical
    total_slack
    total_slack_computed
    progress
    actual_start_date
    actual_finish_date
  }

  fragment feature_instance_spec_sectionFragment on feature_instance_spec_section {
    spec_section {
      title
      section_number
    }
  }

  fragment featuresFragment on features {
    id
  }
`;

export const ENTITIES_LINKING_INFO = gql`
  query getEntityLinksDetails($feature_instance_ids: [uuid!]) {
    feature_instance(where: { id: { _in: $feature_instance_ids } }) {
      id
      auto_inc_value
      title
      feature_type_id

      feature_instance_spec_sections {
        ...feature_instance_spec_sectionFragment
      }

      feature_workflow_instance {
        workflow_status
      }

      feature_submittals {
        material_tracking
      }

      links_as_source(
        where: { target_feature_instance: { deleted: { _eq: false } } }
        order_by: {
          target_feature_instance: { number: asc, auto_inc_value: asc }
        }
      ) {
        id
        governing_link
        created_at
        link_type
        offset_duration
        linked_offset_date
        reverse_dependency
        recommended_by_system
        target_feature_instance {
          ...feature_instanceFragment

          links_as_source(
            where: {
              target_feature_instance: {
                feature_type_id: { _eq: 9 }
                deleted: { _eq: false }
                id: { _nin: $feature_instance_ids }
              }
            }
            order_by: {
              target_feature_instance: { number: asc, auto_inc_value: asc }
            }
          ) {
            id
            link_type
            offset_duration
            governing_link
            linked_offset_date
            reverse_dependency
            recommended_by_system
            target_feature_instance {
              ...feature_instanceFragment
            }
          }

          links_as_target(
            where: {
              source_feature_instance: {
                feature_type_id: { _eq: 9 }
                deleted: { _eq: false }
                id: { _nin: $feature_instance_ids }
              }
            }
            order_by: {
              source_feature_instance: { number: asc, auto_inc_value: asc }
            }
          ) {
            id
            link_type
            offset_duration
            governing_link
            linked_offset_date
            reverse_dependency
            recommended_by_system
            source_feature_instance {
              ...feature_instanceFragment
            }
          }
        }
      }

      links_as_target(
        where: { source_feature_instance: { deleted: { _eq: false } } }
        order_by: {
          source_feature_instance: { number: asc, auto_inc_value: asc }
        }
      ) {
        id
        governing_link
        created_at
        link_type
        offset_duration
        linked_offset_date
        reverse_dependency
        recommended_by_system
        source_feature_instance {
          ...feature_instanceFragment
        }
      }

      # Materials linked as SOURCE (this feature links to a material)
      predecessor_material_links: links_as_target(
        where: {
          source_feature_instance: {
            feature_type_id: { _eq: 2 }
            deleted: { _eq: false }
            implicit: { _eq: false }
            id: { _nin: $feature_instance_ids } # exclude self-links
          }
        }
        order_by: {
          source_feature_instance: { number: asc, auto_inc_value: asc }
        }
      ) {
        id
        link_type
        governing_link
        offset_duration
        linked_offset_date
        reverse_dependency
        recommended_by_system
        created_at
        source_feature_instance {
          ...feature_instanceFragment
        }
      }

      # Materials linked as TARGET (a material links to this feature)
      successor_material_links: links_as_source(
        where: {
          target_feature_instance: {
            feature_type_id: { _eq: 2 }
            deleted: { _eq: false }
            implicit: { _eq: false }
            id: { _nin: $feature_instance_ids } # exclude self-links
          }
        }
        order_by: {
          target_feature_instance: { number: asc, auto_inc_value: asc }
        }
      ) {
        id
        link_type
        governing_link
        offset_duration
        linked_offset_date
        reverse_dependency
        recommended_by_system
        created_at
        target_feature_instance {
          ...feature_instanceFragment
        }
      }
    }
  }

  fragment feature_instance_spec_sectionFragment on feature_instance_spec_section {
    spec_section {
      title
      section_number
    }
  }

  fragment featuresFragment on features {
    id
  }

  fragment feature_instanceFragment on feature_instance {
    id
    auto_inc_value
    number
    title
    deleted_from_source
    implicit
    feature {
      ...featuresFragment
    }
    feature_workflow_instance {
      workflow_status
    }
    feature_instance_spec_sections {
      ...feature_instance_spec_sectionFragment
    }
    feature_activities {
      ...feature_activitiesFragment
    }
    feature_submittals {
      material_tracking
    }
  }

  fragment feature_activitiesFragment on feature_activities {
    type
    duration
    end_date
    start_date
    import_log_id
    parent
    critical
    total_slack
    total_slack_computed
    progress
    actual_start_date
    actual_finish_date
  }
`;

export const FEATURE_ACTIVITIES = gql`
  query GetFeatureActivities {
    feature_activities(
      where: { feature_instance: { deleted: { _eq: false } } }
      order_by: { source_task_id: asc }
    ) {
      feature_instance_id
      feature_instance {
        id
        title
        number
      }
      type
      duration
      end_date
      start_date
      import_log_id
      parent
      critical
      total_slack
      total_slack_computed
      progress
      actual_start_date
      actual_finish_date
    }
  }
`;

export const QUERY_SCHEDULE_VERSIONS = gql`
  query GetScheduleVersions {
    schedule_import_details(
      order_by: { created_at: desc }
      where: { import_log: { import_type: { _eq: SCHEDULE } } }
    ) {
      id
      import_log_id
      is_active
      schedule_name
      schedule_date
      schedule_type
      activated_at
      created_at
      import_log {
        id
        resolved_values_s3_prefix
        source_values_s3_prefix
        created_at
        created_by_user {
          id
          first_name
          last_name
        }
      }
    }
  }
`;
